import { useEffect, useState, useMemo } from "react";
import { Dispatch } from "redux";
import { batch, useSelector } from "react-redux";
import { get, merge } from "lodash";
import { useQuery } from "@apollo/client";
import { RaceStatusTypeCode } from "@tvg/ts-types/Race";
import { ITrack } from "@fdr/types/ts/Derby";
import { HomepageItem, HomepageOrder } from "@fdr/types/ts/Home";
import { isXSell } from "@tvg/sh-utils/mobileUtils";
import { isFDR } from "@tvg/utils/generalUtils";
// @ts-ignore
import DerbyQuery from "../graphql/query/DerbyQuery.graphql";
import ApolloOptions from "../graphql/options.graph";
import componentMap from "../constraints/componentMap";
import { ComponentSorted, DerbyRaceProps, ValidComponentTypes } from "./types";
import { ComponentType, ComponentMapOptions } from "../constraints/types";
import { HomepageConfigOrder, HomepageConfigType } from "../utils/types";
import { getEventType } from "../utils/getEventType";
import { setHomepageDerbyRace, setHomepageEventStatus } from "../redux/actions";
import {
  getUseTopRacesDS,
  getUrpTipsAndPicksToggle,
  getEnableNewUpcomingRaces
} from "../redux/selectors";

const getCurrentType = (
  todayDate?: Date,
  eventDate?: Date | null
): HomepageConfigType => getEventType(todayDate, eventDate);

const getCurrentOrder = (
  homepageOrder: HomepageConfigOrder,
  eventType: HomepageConfigType
): HomepageOrder =>
  get(homepageOrder, eventType, homepageOrder.default as HomepageOrder);

const formatHomepageComponent =
  (options?: ComponentMapOptions) =>
  (
    components: ComponentSorted,
    [sectionKey, sectionItems]: [string, Array<HomepageItem>]
  ) => {
    const isXSellProduct = isXSell();
    const isFDRProduct = isFDR();

    const sectionItem = (
      Array.isArray(sectionItems) ? sectionItems : []
    ).reduce<ComponentSorted>(
      (sectionComponents, sectionComponent) => {
        const currentComponent = get(
          componentMap(options),
          sectionComponent.name
        );
        let sectionType: ValidComponentTypes =
          sectionKey === "header" ? ComponentType.FIXED : ComponentType.FULL;

        if (
          !currentComponent ||
          (isXSellProduct && sectionComponent.hide_on_xsell) ||
          (isFDRProduct && sectionComponent.hide_on_fdr)
        ) {
          return sectionComponents;
        }

        // This only exists to keep PES working
        if (sectionKey === "unmapped") {
          const componentType = get(currentComponent, "type");
          const validTypes = [
            ComponentType.FULL,
            ComponentType.TOP,
            ComponentType.FIXED
          ];
          sectionType = validTypes.includes(componentType)
            ? (componentType as ValidComponentTypes)
            : ComponentType.FULL;
        }

        sectionComponents[sectionType].push({
          type: sectionType,
          component: currentComponent.component,
          context: currentComponent.context,
          isStandalone: currentComponent.isStandalone,
          withoutHeaderSpace: !!currentComponent.withoutHeaderSpace,
          name: sectionComponent.name,
          props: currentComponent.props,
          subtitle: sectionComponent.subtitle,
          title: sectionComponent.description,
          withoutBackground: sectionComponent.without_background,
          headerLink: currentComponent.headerLink
        });

        return sectionComponents;
      },
      {
        fixed: [],
        top: [],
        full: []
      }
    );

    return merge(components, sectionItem);
  };

const getValidType = (
  type: HomepageConfigType,
  raceStatus: RaceStatusTypeCode,
  loading: boolean,
  raceStatusCached: RaceStatusTypeCode
) =>
  (raceStatusCached || raceStatus) === "RO" || (loading && !raceStatusCached)
    ? HomepageConfigType.DEFAULT
    : type;

export const useComponentSorted = (
  homepageOrder: HomepageConfigOrder,
  dispatch: Dispatch,
  {
    todayDate,
    eventDate,
    trackAbbr,
    raceNumber,
    cachedDerbyRace
  }: Partial<DerbyRaceProps> = {}
) => {
  const useTopRacesDS = useSelector(getUseTopRacesDS);
  const enableURPTipsAndPicks = useSelector(getUrpTipsAndPicksToggle);
  const useUpcomingRacesDS = useSelector(getEnableNewUpcomingRaces);

  const defaultOrder = Object.entries(
    homepageOrder.default as HomepageOrder
  ).reduce<ComponentSorted>(formatHomepageComponent(), {
    fixed: [],
    top: [],
    full: []
  });
  const [componentSort, setComponentSort] =
    useState<ComponentSorted>(defaultOrder);

  const currentType = useMemo(
    () => getCurrentType(todayDate, eventDate),
    [todayDate, eventDate]
  );
  const cachedDerbyRaceStatus = get(cachedDerbyRace, "status.code");

  const { data, loading } = useQuery<ITrack>(
    DerbyQuery,
    ApolloOptions({
      trackAbbr,
      eventStatus: currentType,
      raceNumber
    })
  );

  const race = get(data, "track.races[0]", null);
  const raceStatus: RaceStatusTypeCode = get(race, "status.code");

  const currentOrder = useMemo(
    () =>
      getCurrentOrder(
        homepageOrder,
        getValidType(currentType, raceStatus, loading, cachedDerbyRaceStatus)
      ),
    [homepageOrder, currentType, raceStatus, loading, cachedDerbyRaceStatus]
  );

  useEffect(() => {
    const componentsSorted = Object.entries(
      currentOrder
    ).reduce<ComponentSorted>(
      formatHomepageComponent({
        topRacesDS: { enabled: useTopRacesDS },
        tipsAndPicks: { enabled: enableURPTipsAndPicks },
        upcomingRaces: { enabled: useUpcomingRacesDS }
      }),
      {
        fixed: [],
        top: [],
        full: []
      }
    );

    setComponentSort(componentsSorted);
  }, [JSON.stringify(currentOrder)]);

  useEffect(() => {
    batch(() => {
      dispatch(
        setHomepageEventStatus(
          getValidType(
            currentType,
            cachedDerbyRaceStatus || raceStatus,
            loading,
            cachedDerbyRaceStatus
          )
        )
      );
      dispatch(setHomepageDerbyRace(race));
    });
  }, [currentType, raceStatus, cachedDerbyRaceStatus, loading]);

  return componentSort;
};
